<script setup lang="tsx">
import ImageGallery from '~/components/global/ImageGallery.vue'
import Container from '~/components/layout/Container.vue'
import type { Article, ArticleImage } from '~/types/api/Article'

const { t } = useI18n()
const props = defineProps<{
  article: Article
}>()

const vNodes = await convertHtmlStringToVNodes(props.article.description, {
  'sc-custom-gallery': (el, index) => {
    const galleryIndex = el.dataset.galleryIndex
    const galleryImages = props.article.article_images
      .filter(
        (image: ArticleImage) => image.gallery.toString() === galleryIndex
      )
      .map((image: ArticleImage) => image.path)

    if (!galleryImages.length) {
      return h(
        'div',
        { class: 'sc-empty-gallery' },
        t('pages.news.detail.emptyGallery', {
          _0: galleryIndex,
        })
      )
    }

    return h(ImageGallery, {
      galleryId: `gallery-${galleryIndex}-${index}`,
      images: galleryImages,
    })
  },
})

const descriptionVNode = h('div', { class: 'sc-content' }, vNodes)
</script>

<template>
  <div class="c-news-detail-content">
    <Container class="c-news-detail-content__container" variant="narrower">
      <component :is="descriptionVNode" />
    </Container>
  </div>
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;
@use '@/assets/styles/mixins/news-content' as contentMixin;

.c-news-detail-content {
  font-size: 20px;
  line-height: 1.46;
  font-weight: 400;

  @include breakpoint-down('xs') {
    font-size: 18px;
  }

  :deep(.c-image-gallery) {
    margin: 0 0 40px;

    @include breakpoint-down('md') {
      margin: 0 0 30px;
    }

    .sc-overlay {
      font-size: 58px;
    }
  }
}

:deep(.sc-content) {
  @include contentMixin.news-content;
}

:deep(.sc-empty-gallery) {
  width: 100%;
  display: grid;
  place-items: center;
  padding: 50px;
  margin-bottom: 40px;
  background-color: var(--color-green-primary-light-2);
  font-size: 15px;
  font-weight: 600;
  color: var(--color-primary);
}
</style>
